import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

import {
  Client,
  MeterModelDataOut,
  MeterModelGetAllDataIn,
  PageInfo,
  SortingType,
} from '../../../../api/api-reference';
import { PermissionsEnum } from '../../../../api/api-reference';
import { AuthService } from '../../../../services/auth.service';
import { ClipboardService } from '../../../../services/clipboard.service';
import { SearchComponent } from '../../../core/components/search/search.component';
import { SortComponent } from '../../../core/components/sort/sort.component';
import { PaginationComponent } from '../../../core/components/pagination/pagination.component';
import { MeterModelSetupComponent } from './meter-model-setup/meter-model-setup.component';
import { ConfirmDialogComponent } from '../../../core/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-meter-model-management',
  standalone: true,
  imports: [
    MeterModelSetupComponent,
    CommonModule,
    SortComponent,
    SearchComponent,
    TranslateModule,
    PaginationComponent,
    ConfirmDialogComponent,
  ],
  templateUrl: './meter-model-management.component.html',
  styleUrl: './meter-model-management.component.scss',
})
export class MeterModelManagementComponent {
  data: MeterModelDataOut[] | undefined = [];
  isDeleteModalOpen: boolean = false;
  rowDelete: string | undefined = undefined;
  public pageSize: number = 10;
  public pageNumber: number = 1;
  public lastPageSize: number = 1;
  public currentPage: number = 1;
  public numberOfMeterModels: number = 0;
  public PermissionsEnum = PermissionsEnum;
  isAddClicked = false;
  isModalOpen = false;
  selectedRowId: string | null = null;

  requestData = new MeterModelGetAllDataIn({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    search: undefined,
    sorting: SortingType.Descending,
  });

  constructor(
    private client: Client,
    private toastr: ToastrService,
    public authService: AuthService,
    public router: Router,
    public clipboardService: ClipboardService,
  ) {
    this.reloadTable(1);
  }

  handleRowClick(meterModelId?: string) {
    this.selectedRowId = meterModelId!;
    if (!this.isModalOpen) {
      this.isAddClicked = true;
    }
  }

  onSortOption(selectedSort?: SortingType) {
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.reloadTable();
  }

  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;

    this.client.meterModelQuery(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfMeterModels = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfMeterModels / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
    this.selectedRowId = null;
  }

  searchChange(searchText: string) {
    this.requestData.search = searchText;
    this.reloadTable(1);
  }

  handleDelete(id: string) {
    this.rowDelete = id;
    this.isDeleteModalOpen = true;
  }

  confirmDelete() {
    this.client.deleteMeterModel(this.rowDelete).subscribe({
      next: () => {
        this.toastr.success('Meter model successfully removed.');
        this.reloadTable(this.currentPage);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
    this.isDeleteModalOpen = false;
    this.rowDelete = undefined;
  }
  cancelDelete() {
    this.rowDelete = undefined;
    this.isDeleteModalOpen = false;
  }
  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  toggleModal(reload?: boolean) {
    this.selectedRowId = null;
    this.isAddClicked = !this.isAddClicked;
    if (reload) {
      this.reloadTable();
    }
  }

  handleEventCodes(row: MeterModelDataOut) {
    if (row.id)
      this.router.navigate(['/settings/crud/meter-model-event-codes/' + row.id], {
        state: { model: row.model, firmwareVersion: row.firmwareVersion }
      });
  }

  handleEditAssociation(row: MeterModelDataOut) {
    if (row.id)
      this.router.navigate(['/settings/crud/meter-model-version/' + row.id], {
        state: { model: row.model, firmwareVersion: row.firmwareVersion }
      });
  }
}
